import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Check, HeartHandshake, Loader2, MapPin, Minus, Plus, Ticket, X } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { isValidEmail } from "../lib/formHelpers";
import { garderPanier, lirePanier } from "../lib/repriseBillets";

/* Billets des spectacles de juin 2027 (école, 30.10.2026) : des billets pour une ou
   plusieurs représentations des deux spectacles dans la même commande (le panier), puis un
   seul paiement (TWINT) sur la page Payrexx. Devenir bénévole se fait dans le même panier
   (Jen, 30.10.2026) : un rôle par représentation, autant de représentations qu'on veut, et
   chaque créneau offre un billet plein tarif, déduit du total. Les places et les prix
   viennent du serveur (/api/billetterie), qui décide de tout. */

const chf = (n) => `CHF ${Number(n || 0).toFixed(2)}`;
const SANS_BILLET = "aucun";           // un bénévole qui ne veut pas de billet
const ROSE = "#DB2777";
const majuscule = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : "");

const Bandeau = ({ children, ton = "rouge" }) => (
  <div className="p-4 rounded-2xl text-sm flex items-start gap-2"
    style={ton === "rouge" ? { background: "#FEF2F2", color: "#B91C1C" } : { background: "#F0FDF4", color: "#15803D" }}>
    <AlertCircle size={16} className="shrink-0 mt-0.5" /> <div>{children}</div>
  </div>
);

/* Des compteurs compacts : trois par représentation, sur une ligne (téléphone : en dessous). */
const MiniCompteur = ({ label, prix, valeur, onChange, max, testid }) => (
  <div className="flex items-center justify-between sm:justify-start gap-2 sm:flex-col sm:items-center sm:gap-1 min-w-[96px]">
    <div className="text-xs leading-tight sm:text-center"><span className="font-medium">{label}</span><br /><span className="text-[var(--muted)]">{prix}</span></div>
    <div className="flex items-center gap-2">
      <button type="button" className="w-8 h-8 rounded-full border grid place-items-center disabled:opacity-30" style={{ borderColor: "var(--border)" }}
        onClick={() => onChange(valeur - 1)} disabled={valeur <= 0} aria-label={`Un billet ${label} de moins`} data-testid={`${testid}-moins`}><Minus size={14} /></button>
      <span className="w-5 text-center font-semibold tabular-nums" data-testid={`${testid}-nombre`}>{valeur}</span>
      <button type="button" className="w-8 h-8 rounded-full border grid place-items-center disabled:opacity-30" style={{ borderColor: "var(--border)" }}
        onClick={() => onChange(valeur + 1)} disabled={valeur >= max} aria-label={`Un billet ${label} de plus`} data-testid={`${testid}-plus`}><Plus size={14} /></button>
    </div>
  </div>
);

/* En haut de la page (Jen, 30.10.2026) : donner envie d'être bénévole avant même de prendre
   ses billets — vivre le spectacle de l'intérieur, et un billet offert.
   Jamais replié, et le même texte partout : sur téléphone (et donc dans l'app des familles)
   comme sur ordinateur (Jen, 02.11.2026 au soir : « on peut garder la même chose que le
   site » ; la version courte du matin est retirée). */
const AppelBenevoles = ({ onOui }) => (
  <section className="rounded-3xl p-5 sm:p-6 md:p-8 relative overflow-hidden" data-testid="billets-appel-benevoles"
    style={{ background: "linear-gradient(135deg, #FFF7ED 0%, #FDF2F8 100%)", border: "1px solid #FBCFE8" }}>
    <div className="flex items-start gap-4">
      <span className="hidden sm:grid w-12 h-12 rounded-full place-items-center shrink-0" style={{ background: "#fff", color: ROSE }}><HeartHandshake size={24} /></span>
      <div className="min-w-0 space-y-3">
        <h2 className="font-display text-[22px] sm:text-2xl leading-tight">Et si tu vivais le spectacle de l'intérieur ?</h2>
        {/* Le texte entier partout, téléphone et app compris (Jen, 02.11.2026 au soir : « on peut
            garder la même chose que le site »). */}
        <div className="space-y-3" data-testid="billets-appel-texte">
          <p className="text-[15px] leading-relaxed">
            Derrière chaque représentation, il y a une équipe. Coiffer et maquiller les artistes, accueillir le public, veiller sur les enfants en coulisses,
            tenir la tombola ou le stand de pâtisseries : quelques heures de ton temps, et tu fais partie de la magie.
            Tu verras les artistes se préparer, tu rencontreras d'autres familles, et tu offriras aux enfants un souvenir qu'ils garderont longtemps.
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <li>✨ Les coulisses, l'énergie et l'émotion d'avant-spectacle</li>
            <li>🤝 Une équipe chaleureuse, organisée dans un groupe WhatsApp</li>
            <li>🎟️ Un billet offert par créneau, déduit directement de ta commande</li>
            <li>💛 Pas besoin d'être parent d'élève : grands-parents, amis, voisins bienvenus</li>
          </ul>
        </div>
        <button type="button" className="btn-primary w-full sm:w-auto justify-center" style={{ background: ROSE, boxShadow: "0 6px 16px rgba(219,39,119,0.25)" }}
          onClick={onOui} data-testid="billets-vers-benevoles">
          <HeartHandshake size={16} /> Je deviens bénévole
        </button>
      </div>
    </div>
  </section>
);

export default function BilletsSpectacles() {
  useSEO({
    title: "Billets · spectacles de juin 2027 — 1000 ARTS",
    description: "Héros & Vilains (12 et 13 juin 2027, Thierrens) et Le Monde de l'Autre (19 et 20 juin 2027, Goumoens-la-Ville) : achète tes billets en ligne avec TWINT.",
    canonical: "/spectacles/billets",
  });
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const [p, setP] = useState(null);
  const [indisponible, setIndisponible] = useState(false);
  const spectacleId = params.get("spectacle") || "";
  // Le panier (Jen, 30.10.2026) : des billets pour plusieurs représentations et les deux
  // spectacles dans la même commande — { représentation: { plein, enfant, bebe } } — et les
  // créneaux de bénévole — { représentation: { role, billet } }.
  const [panier, setPanier] = useState({});
  const [creneaux, setCreneaux] = useState({});
  // Bénévole ? La section reste repliée tant qu'on n'a pas dit oui (Jen, 30.10.2026 : beaucoup
  // d'information pour ceux que ça n'intéresse pas). Le bénévole peut être quelqu'un d'autre
  // que la personne qui commande, comme une adresse de livraison.
  const [benevoleOui, setBenevoleOui] = useState(null);
  const [moiBenevole, setMoiBenevole] = useState(true);
  const [autre, setAutre] = useState({ prenom: "", nom: "", telephone: "", email: "" });
  // Venu de l'app des familles : le nom, l'e-mail et le téléphone du compte, déjà remplis.
  const [contact, setContact] = useState(() => {
    const base = { prenom: "", nom: "", email: "", telephone: "" };
    try { return { ...base, ...JSON.parse(sessionStorage.getItem("billets_contact") || "{}") }; } catch { return base; }
  });
  const [remarque, setRemarque] = useState("");
  const [accepte, setAccepte] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  const [fait, setFait] = useState("");
  // « Recommencer ma commande » (page « merci », paiement annulé) : le panier revient rempli.
  const [repris, setRepris] = useState(false);
  const reprise = useRef(false);
  // La barre « Total · Continuer » du téléphone se cache quand le formulaire est à l'écran.
  const [formulaireVu, setFormulaireVu] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billetterie`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(setP).catch(() => setIndisponible(true));
  }, []);
  // « Billets » d'un spectacle (page Spectacles) mène à ce spectacle ; « Devenir bénévole »,
  // directement au choix des créneaux.
  const ouvrirBenevoles = () => {
    setBenevoleOui(true);
    setTimeout(() => document.getElementById("benevoles")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };
  useEffect(() => { if (hash === "#benevoles") setBenevoleOui(true); }, [hash]);
  useEffect(() => {
    if (!p || reprise.current || !params.get("reprendre")) return;
    reprise.current = true;
    const s = lirePanier();
    if (!s) return;
    // Seulement ce qui est encore en vente ; le serveur revérifie les places au paiement.
    const ouvertes = new Set(p.representations.filter((r) => r.ouverte).map((r) => r.id));
    setPanier(Object.fromEntries(Object.entries(s.panier || {}).filter(([id]) => ouvertes.has(id))));
    const cr = p.benevoles_ouverts ? Object.fromEntries(Object.entries(s.creneaux || {})
      .filter(([id, c]) => ouvertes.has(id) && c && (c.billet === SANS_BILLET || ouvertes.has(c.billet)))) : {};
    setCreneaux(cr);
    if (Object.keys(cr).length) {
      setBenevoleOui(true);
      setMoiBenevole(s.moiBenevole !== false);
      if (s.autre) setAutre((a) => ({ ...a, ...s.autre }));
    }
    if (s.contact) setContact((c) => ({ ...c, ...s.contact }));
    if (s.remarque) setRemarque(s.remarque);
    setRepris(true);
    setTimeout(() => document.getElementById("commande")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  }, [p]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!p) return undefined;
    // « Billets » d'un spectacle arrive en haut de la page (Jen, 31.10.2026 : l'appel aux
    // bénévoles d'abord) ; le spectacle choisi reste encadré. Seul « Devenir bénévole » descend.
    const cible = hash === "#benevoles" ? "benevoles" : "";
    if (!cible) return undefined;
    const t = setTimeout(() => document.getElementById(cible)?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    return () => clearTimeout(t);
  }, [p, hash, spectacleId]);

  const vide = { plein: 0, enfant: 0, bebe: 0 };
  // Les billets offerts aux bénévoles, par représentation : chacun remplace un billet plein
  // tarif déjà choisi pour cette date, ou s'ajoute (comme le serveur).
  const offerts = useMemo(() => {
    const o = {};
    Object.values(creneaux).forEach((c) => { if (c.role && c.billet !== SANS_BILLET) o[c.billet] = (o[c.billet] || 0) + 1; });
    return o;
  }, [creneaux]);
  const lignes = useMemo(() => (p?.representations || [])
    .map((r) => {
      const q = panier[r.id] || vide;
      const o = offerts[r.id] || 0;
      return { rep: r, ...q, plein: Math.max(q.plein, o), offerts: o };
    })
    .filter((l) => l.plein + l.enfant + l.bebe > 0), [p, panier, offerts]); // eslint-disable-line react-hooks/exhaustive-deps
  // Une date choisie compte quand son rôle l'est aussi.
  const mesCreneaux = (p?.representations || []).filter((r) => creneaux[r.id]?.role).map((r) => ({ rep: r, ...creneaux[r.id] }));
  const sansRole = (p?.representations || []).filter((r) => creneaux[r.id] && !creneaux[r.id].role);
  const benevole = mesCreneaux.length > 0;
  const prix = (l) => (l.plein - l.offerts) * p.tarifs.plein + l.enfant * p.tarifs.enfant;
  const total = p ? lignes.reduce((t, l) => t + prix(l), 0) : 0;
  const nOfferts = lignes.reduce((t, l) => t + l.offerts, 0);
  const lignesOk = (lignes.length > 0 || benevole) && lignes.every((l) => l.plein + l.enfant > 0 && l.bebe <= l.plein);
  const telOk = !benevole || !moiBenevole || contact.telephone.replace(/\D/g, "").length >= 9;
  const autreOk = !benevole || moiBenevole || (autre.prenom.trim() && autre.nom.trim()
    && autre.telephone.replace(/\D/g, "").length >= 9 && (!autre.email.trim() || isValidEmail(autre.email)));
  const pret = lignesOk && contact.prenom.trim() && contact.nom.trim() && isValidEmail(contact.email) && telOk && autreOk
    && !sansRole.length && accepte;
  // Ce qui manque pour continuer, dit en clair (comme le formulaire du cours d'essai).
  const manquants = [
    !lignesOk && "un adulte (plein tarif) avec chaque enfant de 0 à 2 ans",
    (!contact.prenom.trim() || !contact.nom.trim()) && "ton prénom et ton nom",
    !isValidEmail(contact.email) && "une adresse e-mail valide (tes billets y arrivent)",
    !telOk && "ton numéro de téléphone (pour le groupe WhatsApp des bénévoles)",
    !autreOk && "le prénom, le nom et le téléphone du bénévole",
    ...sansRole.map((r) => `choisir ton rôle de bénévole pour le ${r.quand}`),
    !accepte && "cocher « J'accepte les conditions »",
  ].filter(Boolean);
  const changer = (repId, k, v) => setPanier((x) => {
    const q = { ...(x[repId] || vide), [k]: Math.max(0, v) };
    return { ...x, [repId]: q };
  });
  const retirer = (repId) => setCreneaux(({ [repId]: _retire, ...reste }) => reste);
  const titreDe = (r) => p.spectacles.find((s) => s.id === r.spectacle)?.titre || "";
  const libelleRole = (rep, role) => rep.roles.find((x) => x.role === role)?.libelle || role;

  const payer = async (e) => {
    e.preventDefault();
    setErreur(""); setEnvoi(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billetterie/commandes`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lignes: Object.entries(panier).filter(([, q]) => q.plein + q.enfant + q.bebe > 0)
            .map(([id, q]) => ({ representation_id: id, plein: q.plein, enfant: q.enfant, bebe: q.bebe })),
          creneaux: mesCreneaux.map((c) => ({ representation_id: c.rep.id, role: c.role, billet_representation_id: c.billet })),
          benevole: mesCreneaux.length && !moiBenevole ? autre : null,
          remarque, ...contact, conditions_acceptees: accepte, site_web: "",
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "La commande n'a pas pu être enregistrée.");
      if (d.paiement_url) {
        // Gardé sur ce navigateur : si le paiement est annulé, « Recommencer ma commande »
        // retrouve tout (billets, bénévolat, coordonnées).
        garderPanier({ commande: d.commande?.id || "", panier, creneaux, contact, remarque, moiBenevole, autre });
        window.location.assign(d.paiement_url);
        return;
      }
      setFait(d.message);                          // bénévole sans billet : rien à payer
      setEnvoi(false);
      document.getElementById("commande")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) { setErreur(err.message); setEnvoi(false); }
  };

  const retour = params.get("echec") || params.get("annule");
  const formulaire = p && (p.ventes_ouvertes || p.benevoles_ouverts);
  const bouton = total > 0 ? `Payer ${chf(total)} avec TWINT` : lignes.length ? "Confirmer (tout est offert)" : "Je m'inscris comme bénévole";

  return (
    <div data-testid="page-billets" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10 space-y-8">
        <div>
          <Link to="/spectacles" className="btn-ghost mb-6"><ArrowLeft size={16} /> Tous les spectacles</Link>
          <div className="eyebrow" style={{ color: "var(--primary)" }}>Spectacles de juin 2027</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Billets</h1>
          {/* Deux lignes (Jen, 01.11.2026 : sur téléphone, la page était trop longue avant le
              premier compteur). Le reste est là où on en a besoin : les portes et le placement
              libre sous chaque date, le 0-2 ans dans son compteur, tout dans la confirmation. */}
          <p className="text-[var(--muted)] mt-3 max-w-2xl" data-testid="billets-intro">
            Plusieurs dates, un seul paiement TWINT. Élèves sur scène&nbsp;: pas besoin de billet.
          </p>
        </div>

        {p && p.benevoles_ouverts && <AppelBenevoles onOui={ouvrirBenevoles} />}
        {retour && <Bandeau>Le paiement n'a pas abouti : ta commande n'est pas passée et rien n'a été débité. Tu peux réessayer.</Bandeau>}
        {repris && <Bandeau ton="vert">Ta commande est reprise telle quelle : vérifie-la, coche les conditions et paie.</Bandeau>}
        {indisponible && <div className="card-flat text-center" data-testid="billets-bientot"><Ticket className="mx-auto mb-3" /><p>La billetterie des spectacles de juin ouvre bientôt.</p></div>}
        {!p && !indisponible && <div className="py-16 grid place-items-center"><Loader2 className="animate-spin" /></div>}
        {p && !p.ventes_ouvertes && <p className="text-sm" style={{ color: "#B45309" }} data-testid="billets-pas-ouverts">La vente des billets ouvre bientôt.</p>}

        {p && p.spectacles.map((s) => (
          <section key={s.id} id={`spectacle-${s.id}`} className="card-flat !p-0 overflow-hidden scroll-mt-28" data-testid={`billets-spectacle-${s.id}`}
            style={spectacleId === s.id ? { outline: "3px solid var(--primary)", outlineOffset: 2 } : undefined}>
            <div className="grid grid-cols-[96px_1fr] sm:grid-cols-[120px_1fr] gap-0 border-b" style={{ borderColor: "var(--border)" }}>
              <img src={s.affiche} alt={`Affiche · ${s.titre}`} className="w-full h-full aspect-[2/3] object-cover" />
              <div className="p-4 sm:p-5">
                <h2 className="font-display text-2xl">{s.titre}</h2>
                {s.synopsis && <p className="text-sm italic mt-2 leading-snug" data-testid={`billets-synopsis-${s.id}`}>{s.synopsis}</p>}
                <div className="text-sm text-[var(--muted)] mt-1">{s.public}</div>
                <div className="text-sm mt-2 flex items-start gap-1.5"><MapPin size={14} className="mt-0.5 shrink-0" />
                  <span>{s.salle}, {s.adresse} · <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${s.salle}, ${s.adresse}`)}`}
                    target="_blank" rel="noopener noreferrer" className="underline">Carte</a></span></div>
              </div>
            </div>
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {p.representations.filter((r) => r.spectacle === s.id).map((r) => {
                const possible = p.ventes_ouvertes && r.ouverte && r.restantes > 0;
                const q = panier[r.id] || vide;
                const o = offerts[r.id] || 0;
                const libres = Math.max(0, r.restantes - Math.max(q.plein, o) - q.enfant);
                const choisi = q.plein + q.enfant + q.bebe + o > 0;
                return (
                  <div key={r.id} className="p-4 sm:px-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`billets-rep-${r.id}`}
                    style={choisi ? { background: "var(--primary-tint)" } : undefined}>
                    <div className="sm:w-56 shrink-0">
                      <div className="font-semibold">{majuscule(r.quand)}</div>
                      <div className="text-xs text-[var(--muted)] mt-0.5">Portes à {r.portes} · placement libre</div>
                      <div className="text-xs mt-1" style={{ color: r.restantes < 50 ? "#B45309" : "var(--muted)" }}>
                        {!r.ouverte ? (r.sur_demande ? "Ouverte si la demande est forte" : "Pas encore ouverte")
                          : r.restantes <= 0 ? "Complet" : `${r.restantes} places restantes`}
                      </div>
                      {o > 0 && (
                        <div className="text-xs mt-1 font-medium" style={{ color: ROSE }} data-testid={`billets-${r.id}-offert`}>
                          <HeartHandshake size={12} className="inline -mt-0.5" />{" "}
                          {q.plein >= o ? `${o > 1 ? `${o} de tes billets` : "Un de tes billets"} plein tarif ${o > 1 ? "sont offerts" : "est offert"} (bénévole)`
                            : `+ ${o} billet${o > 1 ? "s" : ""} plein tarif offert${o > 1 ? "s" : ""} (bénévole)`}
                        </div>
                      )}
                    </div>
                    {possible && (
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:ml-auto">
                        <MiniCompteur label="Plein tarif" prix={`${p.tarifs.plein}.–`} valeur={q.plein} max={q.plein + libres}
                          onChange={(v) => changer(r.id, "plein", v)} testid={`billets-${r.id}-plein`} />
                        <MiniCompteur label="3 à 12 ans" prix={`${p.tarifs.enfant}.–`} valeur={q.enfant} max={q.enfant + libres}
                          onChange={(v) => changer(r.id, "enfant", v)} testid={`billets-${r.id}-enfant`} />
                        {/* Les bébés s'ajoutent librement (Jen, 31.10.2026) : gratuits, sur les genoux d'un
                            adulte, sans place ; la commande demande un adulte par bébé. */}
                        <MiniCompteur label="0 à 2 ans" prix="gratuit · sur les genoux" valeur={q.bebe} max={20}
                          onChange={(v) => changer(r.id, "bebe", v)} testid={`billets-${r.id}-bebe`} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {p && p.benevoles_ouverts && <ChoixBenevoles p={p} creneaux={creneaux} setCreneaux={setCreneaux} oui={benevoleOui}
          onOui={ouvrirBenevoles} onNon={() => { setBenevoleOui(false); setCreneaux({}); }} />}
        {p && !p.benevoles_ouverts && hash === "#benevoles" && (
          <div id="benevoles" className="card-flat text-center" data-testid="benevoles-bientot">
            <HeartHandshake className="mx-auto mb-3 text-[var(--primary)]" />
            <p>Les inscriptions des bénévoles ouvrent bientôt.</p>
          </div>
        )}

        {formulaire && (fait ? (
          <div id="commande" className="scroll-mt-28" data-testid="billets-fait"><Bandeau ton="vert">{fait}</Bandeau></div>
        ) : lignes.length === 0 && !benevole ? (
          <p className="text-sm text-[var(--muted)] text-center" data-testid="billets-panier-vide">
            Choisis tes billets ci-dessus : tu peux en prendre pour plusieurs représentations et pour les deux spectacles, dans la même commande.
            {p.benevoles_ouverts && " Et si tu deviens bénévole, ton billet offert se déduit de la commande."}
          </p>
        ) : (
          <form id="commande" onSubmit={payer} noValidate className="card-flat space-y-6 scroll-mt-28" data-testid="billets-formulaire">
            <div>
              <h2 className="font-display text-2xl">Ta commande</h2>
              {lignes.length > 0 && (
                <ul className="mt-3 divide-y text-sm" style={{ borderColor: "var(--border)" }} data-testid="billets-panier">
                  {lignes.map((l) => (
                    <li key={l.rep.id} className="py-2.5 flex flex-wrap items-baseline justify-between gap-2">
                      <span>
                        <strong>{titreDe(l.rep)}</strong> · {l.rep.quand}
                        <span className="block text-[var(--muted)]">
                          {[l.plein && (l.offerts && l.offerts === l.plein ? `${l.plein} × plein tarif offert${l.plein > 1 ? "s" : ""} (bénévole)`
                            : `${l.plein} × plein tarif${l.offerts ? ` (dont ${l.offerts} offert${l.offerts > 1 ? "s" : ""} · bénévole)` : ""}`),
                            l.enfant && `${l.enfant} × 3 à 12 ans`, l.bebe && `${l.bebe} × 0 à 2 ans (gratuit, sur les genoux, sans place)`].filter(Boolean).join(" · ")}
                          {l.bebe > l.plein && <span className="block" style={{ color: "#B91C1C" }} data-testid={`billets-${l.rep.id}-bebe-sans-adulte`}>
                            Chaque enfant de 0 à 2 ans est sur les genoux d'un adulte : ajoute {l.bebe - l.plein > 1 ? `${l.bebe - l.plein} billets` : "un billet"} plein tarif pour cette date.</span>}
                        </span>
                      </span>
                      <span className="font-semibold tabular-nums">{chf(prix(l))}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {[["prenom", "Prénom"], ["nom", "Nom"], ["email", "E-mail (tes billets y arrivent)"],
                ["telephone", benevole && moiBenevole ? "Téléphone (pour le groupe WhatsApp des bénévoles)" : "Téléphone (facultatif)"]].map(([k, l]) => (
                <label key={k} className="block">
                  <span className="field-label">{l}</span>
                  <input className="input-field" type={k === "email" ? "email" : k === "telephone" ? "tel" : "text"} value={contact[k]}
                    onChange={(e) => setContact({ ...contact, [k]: e.target.value })} data-testid={`billets-${k}`} autoComplete={k === "email" ? "email" : k === "telephone" ? "tel" : k === "prenom" ? "given-name" : "family-name"} />
                </label>
              ))}
              {benevole && (
                <label className="block sm:col-span-2">
                  <span className="field-label">Un mot pour l'école (facultatif)</span>
                  <textarea className="input-field" rows={2} value={remarque} onChange={(e) => setRemarque(e.target.value)} data-testid="billets-remarque" />
                </label>
              )}
            </div>

            {benevole && (
                <div className="rounded-2xl p-4" style={{ background: "#FDF2F8", border: "1px solid #FBCFE8" }} data-testid="billets-creneaux">
                  <div className="font-semibold flex items-center gap-2" style={{ color: "#9D174D" }}>
                    <HeartHandshake size={16} /> Bénévole{(moiBenevole ? contact.prenom : autre.prenom) ? ` : ${moiBenevole ? contact.prenom : autre.prenom}` : ""} · {mesCreneaux.length} créneau{mesCreneaux.length > 1 ? "x" : ""}
                  </div>
                  <ul className="mt-2 divide-y text-sm" style={{ borderColor: "#FBCFE8" }}>
                    {mesCreneaux.map((c) => (
                      <li key={c.rep.id} className="py-2.5 flex flex-wrap items-center gap-2" data-testid={`billets-creneau-${c.rep.id}`}>
                        <span className="flex-1 min-w-[180px]"><strong>{libelleRole(c.rep, c.role)}</strong> · {titreDe(c.rep)}, {c.rep.quand}</span>
                        <label className="order-3 sm:order-none w-full sm:w-auto flex flex-col items-stretch sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs">
                          <span className="shrink-0">Billet offert :</span>
                          {/* La date d'abord (01.11.2026 : sur téléphone, le choix coupé ne montrait que
                              « Héros & Vilains · dim… ») ; le menu prend toute la largeur. */}
                          <select className="input-field !py-1.5 !text-xs w-full sm:w-auto min-w-0" value={c.billet} data-testid={`billets-creneau-${c.rep.id}-billet`}
                            onChange={(e) => setCreneaux((x) => ({ ...x, [c.rep.id]: { ...x[c.rep.id], billet: e.target.value } }))}>
                            {p.representations.filter((r) => r.ouverte).map((r) => <option key={r.id} value={r.id}>{majuscule(r.quand.replace(/ 20\d\d/, ""))} · {titreDe(r)}</option>)}
                            <option value={SANS_BILLET}>pas besoin de billet, merci</option>
                          </select>
                        </label>
                        <button type="button" className="order-2 sm:order-none p-1.5 rounded-full hover:bg-white" onClick={() => retirer(c.rep.id)}
                          aria-label={`Retirer le créneau du ${c.rep.quand}`} data-testid={`billets-creneau-${c.rep.id}-retirer`}><X size={14} /></button>
                      </li>
                    ))}
                  </ul>
                  <label className="flex items-center gap-2 text-sm mt-3 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 accent-[#DB2777]" checked={moiBenevole} onChange={(e) => setMoiBenevole(e.target.checked)} data-testid="billets-benevole-moi" />
                    Le bénévole, c'est moi{contact.prenom.trim() ? ` (${contact.prenom.trim()})` : " (la personne qui commande)"}
                  </label>
                  {!moiBenevole && (
                    <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="billets-benevole-autre">
                      {[["prenom", "Prénom du bénévole"], ["nom", "Nom du bénévole"], ["telephone", "Son téléphone (groupe WhatsApp)"], ["email", "Son e-mail (facultatif : sinon c'est toi qui reçois)"]].map(([k, l]) => (
                        <label key={k} className="block">
                          <span className="field-label">{l}</span>
                          <input className="input-field bg-white" type={k === "email" ? "email" : k === "telephone" ? "tel" : "text"} value={autre[k]}
                            onChange={(e) => setAutre({ ...autre, [k]: e.target.value })} data-testid={`billets-benevole-${k}`} />
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            <div>
              <div className="font-display text-xl mb-3">Confirmation</div>
              <label className="flex items-start gap-3 cursor-pointer p-4 rounded-2xl border-2 transition-colors"
                style={{ borderColor: accepte ? "var(--primary)" : "#FCD34D", background: accepte ? "var(--primary-tint)" : "#FFFBEB" }}>
                <input type="checkbox" className="mt-1 w-5 h-5 accent-[var(--primary)] shrink-0" checked={accepte}
                  onChange={(e) => setAccepte(e.target.checked)} data-testid="billets-conditions" />
                <span className="text-sm"><strong>J'accepte les conditions :</strong> les billets ne sont ni remboursables ni échangeables.
                  Placement libre ; les élèves qui sont sur scène n'ont pas besoin de billet.
                  {benevole && " Bénévole : c'est confirmé tout de suite par e-mail, avec le lien du groupe WhatsApp et un lien pour changer ou annuler ton créneau (jusqu'à 48 h avant)."}</span>
              </label>
            </div>

            {erreur && <Bandeau>{erreur}</Bandeau>}
            {!pret && (
              <div className="p-4 rounded-2xl text-sm" style={{ background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E" }} data-testid="billets-manquants">
                <div className="font-semibold mb-2">Pour continuer, il te reste à&nbsp;:</div>
                <ul className="space-y-1 list-disc pl-5">{manquants.map((m) => <li key={m}>{m}</li>)}</ul>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="text-lg">Total : <strong data-testid="billets-total">{chf(total)}</strong>
                {lignes.length > 1 && <span className="text-sm text-[var(--muted)]"> · {lignes.length} représentations</span>}
                {nOfferts > 0 && <span className="block text-sm" style={{ color: ROSE }} data-testid="billets-deduits">
                  {nOfferts} billet{nOfferts > 1 ? "s" : ""} offert{nOfferts > 1 ? "s" : ""} (bénévole) : − {chf(nOfferts * p.tarifs.plein)}</span>}
              </div>
              <button type="submit" className="btn-primary" disabled={!pret || envoi} data-testid="billets-payer">
                {envoi ? <Loader2 size={16} className="animate-spin" /> : total > 0 ? <Ticket size={16} /> : <HeartHandshake size={16} />} {bouton}
              </button>
            </div>
            {p.paiement_simule && total > 0 && <p className="text-xs text-[var(--muted)]">Version de test : le paiement est simulé, rien n'est débité.</p>}
          </form>
        ))}
      </div>
      {formulaire && !fait && (lignes.length > 0 || benevole) && (
        <BarrePanier total={total} nb={lignes.reduce((t, l) => t + l.plein + l.enfant + l.bebe, 0)} creneaux={mesCreneaux.length}
          cachee={formulaireVu} onVu={setFormulaireVu} />
      )}
    </div>
  );
}

/* Sur téléphone, le panier reste sous le pouce (Jen, 01.11.2026) : une barre en bas, « Total CHF …
   · Continuer », qui mène au formulaire. Elle disparaît dès que le formulaire est à l'écran (il a
   son propre total et son bouton), respecte la zone du bas de l'iPhone, et fait remonter la
   bulle de l'assistant au-dessus d'elle. Sur ordinateur, pas de barre. */
function BarrePanier({ total, nb, creneaux, cachee, onVu }) {
  useEffect(() => {
    const cible = document.getElementById("commande");
    if (!cible || typeof IntersectionObserver === "undefined") return undefined;
    const o = new IntersectionObserver(([e]) => onVu(e.isIntersecting), { rootMargin: "0px 0px -15% 0px" });
    o.observe(cible);
    return () => o.disconnect();
  }, [onVu]);
  const visible = !cachee;
  useEffect(() => {
    document.body.classList.toggle("barre-panier", visible);
    return () => document.body.classList.remove("barre-panier");
  }, [visible]);
  const detail = [nb ? `${nb} billet${nb > 1 ? "s" : ""}` : "", creneaux ? `${creneaux} créneau${creneaux > 1 ? "x" : ""} bénévole` : ""].filter(Boolean).join(" · ");
  return (
    <>
      <style>{`@media (max-width: 639px) {
        body.barre-panier .assistant-site { bottom: calc(5.75rem + env(safe-area-inset-bottom, 0px)) !important; }
        body.barre-panier.depuis-app .assistant-site { bottom: calc(10rem + env(safe-area-inset-bottom, 0px)) !important; }
        body.depuis-app [data-testid=billets-barre] { bottom: calc(4.25rem + env(safe-area-inset-bottom, 0px)) !important; }
      }`}</style>
      <div data-testid="billets-barre" aria-hidden={!visible}
        className="sm:hidden fixed inset-x-3 z-[55] transition-all duration-300"
        style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))", opacity: visible ? 1 : 0,
                 transform: visible ? "translateY(0)" : "translateY(140%)", pointerEvents: visible ? "auto" : "none" }}>
        <div className="flex items-center gap-3 rounded-2xl pl-4 pr-2 py-2 shadow-2xl"
          style={{ background: "rgba(15,27,45,0.94)", color: "#fff", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="text-[11px] uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.65)" }}>Total</div>
            <div className="font-semibold tabular-nums text-[17px]" data-testid="billets-barre-total">{chf(total)}</div>
            {detail && <div className="text-[11px] truncate" style={{ color: "rgba(255,255,255,0.65)" }}>{detail}</div>}
          </div>
          <button type="button" className="btn-primary !py-3 !px-5 shrink-0" tabIndex={visible ? 0 : -1} data-testid="billets-barre-continuer"
            onClick={() => document.getElementById("commande")?.scrollIntoView({ behavior: "smooth", block: "start" })}>
            Continuer
          </button>
        </div>
      </div>
    </>
  );
}

/* Devenir bénévole (Jen, 30.10.2026) : d'abord « Tu veux être bénévole ? » (Oui / Non merci) —
   repliée, la section ne montre rien de plus. Oui : 1. les dates où l'on peut aider, 2. pour
   chaque date, son rôle (une carte par rôle avec sa description et ses places). Un rôle par
   représentation, autant de représentations qu'on veut. Les créneaux vont dans le panier, avec
   les billets ; chacun offre un billet plein tarif. Confirmé d'office par e-mail (01.11.2026). */
const EMOJI = { tombola: "🎟️", patisserie: "🧁", entrees: "🎫", surveillance: "🧒", maquillage: "💄", parking: "🚗" };
const places = (n) => (n > 0 ? `${n} place${n > 1 ? "s" : ""}` : "complet");

function ChoixBenevoles({ p, creneaux, setCreneaux, oui, onOui, onNon }) {
  const reps = p.representations.filter((r) => r.ouverte);
  const n = Object.values(creneaux).filter((c) => c.role).length;
  const libres = (r) => r.roles.reduce((t, x) => t + x.restantes, 0);
  const basculerDate = (r) => setCreneaux((x) => {
    if (x[r.id]) {
      const { [r.id]: _retire, ...reste } = x;
      return reste;
    }
    return { ...x, [r.id]: { role: "", billet: r.id } };
  });
  const choisirRole = (r, role) => setCreneaux((x) => ({ ...x, [r.id]: { ...x[r.id], role } }));
  return (
    <section id="benevoles" className="card-flat space-y-5 scroll-mt-28" data-testid="benevoles">
      <div className="flex items-start gap-3">
        <HeartHandshake className="shrink-0 mt-1" style={{ color: ROSE }} />
        <div>
          <h2 className="font-display text-2xl">Tu veux être bénévole ?</h2>
          <p className="text-sm text-[var(--muted)] mt-1">Un billet plein tarif offert par créneau, déduit de ta commande. Pas besoin d'être parent d'élève.</p>
        </div>
      </div>
      {oui !== true ? (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="btn-primary" style={{ background: ROSE, boxShadow: "0 6px 16px rgba(219,39,119,0.25)" }} onClick={onOui} data-testid="benevoles-oui">
            <HeartHandshake size={16} /> Oui, je veux aider
          </button>
          <button type="button" className="btn-ghost" onClick={onNon} aria-pressed={oui === false} data-testid="benevoles-non">
            {oui === false && <Check size={14} />} Non merci
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="font-semibold">1. Quand peux-tu aider ? <span className="font-normal text-sm text-[var(--muted)]">Une ou plusieurs dates.</span></div>
            <button type="button" className="text-sm underline text-[var(--muted)]" onClick={onNon} data-testid="benevoles-finalement-non">Finalement non</button>
          </div>
          {p.spectacles.map((s) => {
            const rs = reps.filter((r) => r.spectacle === s.id);
            if (!rs.length) return null;
            return (
              <div key={s.id}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)] mb-2">{s.titre}</h3>
                <div className="space-y-2">
                  {rs.map((r) => {
                    const c = creneaux[r.id];
                    const role = c?.role ? r.roles.find((x) => x.role === c.role) : null;
                    const pleine = libres(r) === 0;
                    return (
                      <div key={r.id} className="rounded-2xl border transition-colors" style={c ? { borderColor: ROSE, background: "#FFF7FB" } : { borderColor: "var(--border)" }}
                        data-testid={`benevoles-rep-${r.id}`}>
                        <button type="button" disabled={pleine && !c} onClick={() => basculerDate(r)} aria-pressed={!!c}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left disabled:opacity-50" data-testid={`benevoles-date-${r.id}`}>
                          <span className="w-5 h-5 rounded-md border-2 grid place-items-center shrink-0"
                            style={c ? { background: ROSE, borderColor: ROSE, color: "#fff" } : { borderColor: "#CBD5E1", background: "#fff" }}>{c && <Check size={13} />}</span>
                          <span className="flex-1 font-medium">{majuscule(r.quand)}</span>
                          {role ? <span className="text-sm font-semibold text-right" style={{ color: "#9D174D" }}>{EMOJI[role.role]} {role.libelle}</span>
                            : <span className="text-xs text-[var(--muted)] text-right">{pleine ? "complet" : c ? "choisis ton rôle ↓" : `${libres(r)} places`}</span>}
                        </button>
                        {c && !c.role && (
                          <div className="px-4 pb-4" data-testid={`benevoles-roles-${r.id}`}>
                            <div className="text-sm font-semibold mb-2">2. Ton rôle ce jour-là</div>
                            <div className="grid sm:grid-cols-2 gap-2">
                              {r.roles.map((x) => (
                                <button key={x.role} type="button" disabled={!x.restantes} onClick={() => choisirRole(r, x.role)}
                                  className="rounded-xl border bg-white px-3 py-2.5 text-left disabled:opacity-40 transition-colors hover:border-[#DB2777]"
                                  style={{ borderColor: "var(--border)" }} data-testid={`benevoles-${r.id}-${x.role}`}>
                                  <div className="flex items-baseline justify-between gap-2">
                                    <span className="font-medium">{EMOJI[x.role]} {x.libelle}</span>
                                    <span className="text-xs shrink-0" style={{ color: x.restantes ? "var(--muted)" : "#B91C1C" }}>{places(x.restantes)}</span>
                                  </div>
                                  {x.description && <div className="text-xs text-[var(--muted)] mt-0.5 leading-snug">{x.description}</div>}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {c?.role && (
                          <div className="px-4 pb-3 -mt-1 pl-12 text-xs">
                            <button type="button" className="underline text-[var(--muted)]" onClick={() => choisirRole(r, "")} data-testid={`benevoles-changer-${r.id}`}>changer de rôle</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {n > 0 && (
            <p className="text-sm font-medium" style={{ color: "#9D174D" }} data-testid="benevoles-choisis">
              <Check size={14} className="inline -mt-0.5" /> {n} créneau{n > 1 ? "x" : ""} : {n > 1 ? "ils sont" : "il est"} dans ta commande, juste en dessous.
            </p>
          )}
        </>
      )}
    </section>
  );
}
